// Mock mode serves a fixture PR built from docs/reference/sample-data.json
// instead of calling the server: `?mock=1` on the dev server, or always in a
// build made with VITE_MOCK=1 (the static demo, `pnpm build:demo`). Other
// production builds never include it.
const KEY = 'changelist:mock';

/** The query string mock switches are read from (the hash's in hash-routed builds). */
function mockSearch(): string {
  if (import.meta.env.VITE_ROUTER === 'hash') {
    const q = location.hash.indexOf('?');
    return q < 0 ? '' : location.hash.slice(q);
  }
  return location.search;
}

function detect(): boolean {
  if (import.meta.env.VITE_MOCK === '1') return true;
  if (!import.meta.env.DEV) return false;
  if (typeof location === 'undefined') return false;
  const q = new URLSearchParams(location.search).get('mock');
  try {
    if (q === '1') sessionStorage.setItem(KEY, '1');
    if (q === '0') sessionStorage.removeItem(KEY);
    return sessionStorage.getItem(KEY) === '1';
  } catch {
    return q === '1';
  }
}

export const MOCK: boolean = detect();

/**
 * A mock-mode switch from the query string, remembered for the tab
 * (`?mockfail=SubmitReview,AddThread`, `?mockslow=1500` (every write takes
 * that long), `?mocktrunc=1`,
 * `?mockrole=member`, `?mockunprotected=1`, `?mocksignedout=1`,
 * `?mockthreads=noresolve|noreply`, `?mockslowfiles=ms`; an empty value
 * clears it). `mockfail` also takes `Logout` (POST /auth/logout answers 500)
 * `Attention` (override writes answer 500; `Attention:<login>` only that login's) and `OrgSettings` (reading the
 * org policy answers 500); `?mockslowpolicy=ms` makes the org policy take
 * that long, and `?mockslowattention=ms` reading the attention overrides.
 * `?mockrole=outsider` makes the viewer no member of the org.
 * `?mockviewer=author` signs in as the fixture PR's author; `mockfail`
 * also takes `RequestReviewers` (GitHub refuses a review request).
 * `?mockauthor=CONTRIBUTOR` (any `authorAssociation`, or `missing`) sets the
 * fixture PR author's relationship to the repository (default COLLABORATOR).
 * `?mockintegration=Op1,Op2`
 * (or `all`) fails those GitHub writes with "Resource not accessible by
 * integration"; `?mockinstall=covered|all|other|missing|noperm|none|empty|forbidden|fail|flaky`
 * shapes the App installations the viewer sees; `?mockperm=READ` gives the
 * viewer read access only; `?mockcount=N` makes dashboard searches report N
 * matches. `?mockpending=ms` makes the server's own data answer 503
 * `installations_pending` for that long after the page loads;
 * `?mocknotinstalled=1` makes the server's own data answer 403
 * `not_installed`; `?mockpolicypending=1` makes the org policy pending (not
 * resolved) until an admin saves it. `?mocklinks=privacy,terms,support` (or
 * `all`) configures those operator links for the footer. `?mockdown=502|503|504|network`
 * makes every request fail as when changelist's server is down (for
 * `?mockdownfor=ms` after the page loads, if given), and
 * `?mocktimeout=Op1,Op2` makes those GraphQL operations answer 504
 * `upstream_timeout` (GitHub too slow). `mockfail` also takes `Blob` (reading a
 * file's contents answers 404), and `?mockservererror=<code>` makes GitHub
 * requests through the bridge answer 503 with that typed error.
 * `?mockratelimit=primary|secondary|secondary-unstated` makes GitHub calls
 * meet GitHub's rate limit (see `mockRateLimit` in server.ts).
 * `?mockviewed=VIEWED:path,DISMISSED:path` starts the viewer's GitHub
 * "Viewed" checkboxes in those states (`mockfail` and `mockintegration`
 * also take `MarkFileViewed` and `UnmarkFileViewed`).
 * `mockfail` also takes `HeldWrite` (every draft write answers 500) and
 * `HeldCreate:N` (only the Nth draft created since the page loaded).
 * `?mockdeployments=withheld` answers the deployments query as GitHub does
 * for a commit whose deployments the token may not read: null, beside a
 * partial error.
 * `?mockfail=PullRequestStack` and `?mocktimeout=PullRequestStack` fail the
 * pull request page's stack read alone, and `?mockslowstack=ms` makes it
 * take that long; `?mockghes=1` is a GitHub whose
 * schema has no native stacks (nor `closingIssuesReferences`).
 * `?mockslowload=ms` makes every read take that long (the loading screens).
 * `?mockrounds=1` adds `rounds.rs`, a file the pull request adds whose first
 * snapshot was an earlier version (six changes from that snapshot to the head).
 * `?mockrepoerr=owner/name:sso|forbidden|timeout,…` fails those
 * repositories' counts on the repository index as GitHub does, beside the
 * others' data (mock/repos.ts).
 */
export function mockFlag(name: string): string | null {
  if (typeof location === 'undefined') return null;
  const q = new URLSearchParams(mockSearch()).get(name);
  try {
    if (q !== null) {
      if (q) sessionStorage.setItem('changelist:' + name, q);
      else sessionStorage.removeItem('changelist:' + name);
    }
    return sessionStorage.getItem('changelist:' + name);
  } catch {
    return q || null;
  }
}
